<script setup>
import { reactive, ref } from 'vue';
import { PERMISSIONS } from '~/constants/permissions';

const { hasPermission } = usePermissions();

useHead({
  script: [
    {
      src: 'https://kit.fontawesome.com/a31fdd295d.js',
      crossorigin: 'anonymous',
    },
  ],
});

const { t } = useI18n();
const route = useRoute();

const appVersion = useRuntimeConfig().public.appVersion;

const isOpen = ref(true);
const colorMode = useColorMode();
const logoUrl = ref(
  colorMode.value === 'dark'
    ? '/salesupply-logo_white.png'
    : '/salesupply-logo.svg'
);
const fulfilmentNavItems = reactive([
  {
    name: t('insights.fulfilment.insights.overview.title'),
    slug: 'overview',
    icon: 'fa fa-chart-pie',
    link: '/fulfilment/overview',
    active: true,
    disabled: false,
  },
  {
    name: t('insights.fulfilment.insights.orders.title'),
    slug: 'orders',
    icon: 'fa fa-box',
    link: '/fulfilment/orders',
    active: false,
    disabled: false,
  },
  {
    name: t('insights.fulfilment.insights.shipments.title'),
    slug: 'shipments',
    icon: 'fa fa-truck-fast',
    link: '/fulfilment/shipments',
    active: false,
    disabled: false,
  },
  {
    name: t('insights.fulfilment.insights.parcels.title'),
    slug: 'parcels',
    icon: 'fa fa-boxes',
    link: '/fulfilment/parcels',
    active: false,
    disabled: false,
  },
  {
    name: t('insights.returns.title'),
    slug: 'returns',
    icon: 'fa fa-rotate-left',
    link: '/fulfilment/returns',
    active: false,
    disabled: false,
  },
  {
    name: t('insights.fulfilment.insights.products.title'),
    slug: 'products',
    icon: 'fa fa-cart-shopping',
    link: '/fulfilment/products',
    active: false,
    disabled: false,
  },
  {
    name: t('insights.fulfilment.insights.inventory.title'),
    slug: 'inventory',
    icon: 'fa fa-warehouse',
    link: '/fulfilment/inventory',
    active: false,
    disabled: false,
  },
  {
    name: t('fulfillment_settings.inventory_transfer.title'),
    slug: 'purchase-orders',
    link: '/fulfilment/purchase-orders',
    active: false,
    icon: 'fa fas fa-truck-arrow-right',
  },
]);

// Customer Care nav items - only KPI Overview
const customerCareNavItems = computed(() => {
  return [
    {
      name: t('insights.customer_care.kpi_overview.title'),
      slug: 'kpi-overview',
      link: '/customer-care/kpi-overview',
      active: route.path.includes('/customer-care/kpi-overview'),
      disabled: false,
    },
  ];
});

// Check if Fulfilment menu should be visible (requires OMS shop_owners)
const showFulfilment = computed(() => {
  const clientsStore = useClientsStore();
  const selectedClient = clientsStore.selectedClient;
  if (!selectedClient || !selectedClient.shop_owners) {
    return false;
  }

  return selectedClient.shop_owners.some(
    shopOwner => shopOwner.third_party === 'OMS'
  );
});

// Check if Customer Care menu should be visible (requires CMS shop_owners)
const showCustomerCare = computed(() => {
  // Customer care (KPI) routes are temporarily disabled.
  return false;

  const clientsStore = useClientsStore();
  const selectedClient = clientsStore.selectedClient;

  if (!selectedClient || !selectedClient.shop_owners) {
    return false;
  }

  return selectedClient.shop_owners.some(
    shopOwner => shopOwner.third_party === 'CMS'
  );
});
// Communication Hub is visible to any login with the ticket.index permission.
const showCommunication = computed(() =>
  hasPermission(PERMISSIONS.TICKET.INDEX)
);

// route.name is only set once a route has matched, so it is undefined while a
// redirect is in flight -- which crashed the whole layout. It can also be a
// Symbol for unnamed routes, hence String().
const checkRoute = name => String(route.name ?? '').includes(name);
</script>

<template>
  <div class="salesupply_skeleton d-flex flex-column flex-lg-row h-lg-full">
    <div
      class="d-flex flex-column h-lg-screen"
      style="min-width: 270px !important"
    >
      <header style="position: relative; z-index: 1001">
        <nuxt-link
          class="d-none d-lg-inline-block align-self-center py-3 d-none d-lg-flex align-items-center justify-content-center bg-white w-100"
          style="border-bottom: 1px solid #eef1f7 !important"
          to="/"
          ><img
            :class="{ 'ss-aside__logo__small': !isOpen }"
            :src="logoUrl"
            style="height: 40px !important"
        /></nuxt-link>
      </header>
      <nav
        id="sidebar"
        :class="{ 'show-active': !isOpen }"
        class="navbar show navbar-vertical navbar-expand-lg scrollbar border-bottom border-bottom-lg-0 position-lg-fixed px-0 py-3 h-lg-screen"
      >
        <div class="container-fluid">
          <div
            class="container-fluid justify-content-center d-lg-none pb-2"
            style="padding-left: 0 !important"
          >
            <nuxt-link class="navbar-brand m-0 p-0" to="/"
              ><img
                :class="{ 'ss-aside__logo__small': !isOpen }"
                :src="logoUrl"
                style="height: 30px !important"
            /></nuxt-link>
          </div>
          <button
            aria-controls="sidebarCollapse"
            aria-expanded="false"
            :aria-label="$t('common.a11y.toggle_navigation')"
            class="navbar-toggler ms-n2"
            data-bs-target="#sidebarCollapse"
            data-bs-toggle="collapse"
            type="button"
          >
            <i class="fa-solid fa-bars p-2" />
          </button>
          <div class="navbar-user d-lg-none">
            <div class="hstack gap-1">
              <LayoutPartsCompanies />
              <LayoutPartsAccount />
            </div>
          </div>
          <div id="sidebarCollapse" class="collapse navbar-collapse py-2">
            <ul class="navbar-nav">
              <nuxt-link exact to="/">
                <li
                  :class="{ active: isOpen }"
                  :title="t('dashboard.title')"
                  class="nav-item mb-2"
                  data-bs-toggle="menu-tooltip"
                >
                  <i class="fas fa-th-large" />
                  <span>{{ t('dashboard.title') }}</span>
                </li>
              </nuxt-link>
              <nuxt-link
                v-if="showFulfilment"
                :class="{ 'nuxt-link-active': checkRoute('fulfilment') }"
                to="/fulfilment/overview"
              >
                <li
                  :class="{ active: isOpen }"
                  :title="t('insights.fulfilment.title')"
                  class="nav-item mb-2"
                  data-bs-toggle="menu-tooltip"
                >
                  <i class="fas fa-truck-ramp-box" />
                  <span>{{ t('insights.fulfilment.title') }}</span>
                </li>
              </nuxt-link>

              <li
                v-show="showFulfilment && checkRoute('fulfilment')"
                class="nav-item"
              >
                <div class="collapse show">
                  <ul class="nav nav-sm flex-column nav-items">
                    <nuxt-link
                      v-for="fulfilmentItem in fulfilmentNavItems"
                      :key="fulfilmentItem.slug"
                      :to="fulfilmentItem.link"
                    >
                      <li class="nav-item nav-subitem">
                        <span class="nav-link">{{ fulfilmentItem.name }}</span>
                      </li>
                    </nuxt-link>
                  </ul>
                </div>
              </li>

              <nuxt-link
                v-if="showCustomerCare"
                :class="{ 'nuxt-link-active': checkRoute('customer-care') }"
                to="/customer-care/kpi-overview"
              >
                <li
                  :class="{ active: isOpen }"
                  :title="t('insights.customer_care.title')"
                  class="nav-item mb-2"
                  data-bs-toggle="menu-tooltip"
                >
                  <i class="fas fa-headset" />
                  <span>{{ t('insights.customer_care.title') }}</span>
                </li>
              </nuxt-link>

              <li
                v-show="
                  showCustomerCare &&
                  checkRoute('customer-care') &&
                  customerCareNavItems.length > 0
                "
                class="nav-item"
              >
                <div class="collapse show">
                  <ul class="nav nav-sm flex-column nav-items">
                    <nuxt-link
                      v-for="customerCareItem in customerCareNavItems"
                      :key="customerCareItem.slug"
                      :to="customerCareItem.link"
                      :class="{ 'nuxt-link-active': customerCareItem.active }"
                    >
                      <li class="nav-item nav-subitem">
                        <span
                          class="nav-link"
                          :class="{ active: customerCareItem.active }"
                        >
                          {{ customerCareItem.name }}
                        </span>
                      </li>
                    </nuxt-link>
                  </ul>
                </div>
              </li>
              <nuxt-link
                :class="{ 'nuxt-link-active': checkRoute('reports') }"
                to="/reports"
              >
                <li
                  :class="{ active: isOpen }"
                  :title="t('reports.title')"
                  class="nav-item mb-2"
                  data-bs-toggle="menu-tooltip"
                >
                  <i class="fas fa-chart-simple" />
                  <span>{{ t('reports.title') }}</span>
                </li>
              </nuxt-link>
              <nuxt-link
                v-if="showCommunication"
                :class="{ 'nuxt-link-active': checkRoute('communication') }"
                to="/communication"
              >
                <li
                  :class="{ active: isOpen }"
                  :title="t('communication.title')"
                  class="nav-item mb-2"
                  data-bs-toggle="menu-tooltip"
                >
                  <i class="fas fa-comments" />
                  <span>{{ t('communication.title') }}</span>
                </li>
              </nuxt-link>
            </ul>

            <div class="mt-auto" />

            <hr class="navbar-divider my-4" />
            <div class="position-relative">
              <div class="dropup w-full">
                <ul class="navbar-nav">
                  <a
                    exact
                    href="https://doc.clickup.com/24344414/d/h/q6xuy-88515/ef76a8deea2203d"
                    target="_blank"
                    rel="noopener"
                    style="display: block"
                  >
                    <li
                      :title="t('helpcenter.title')"
                      class="nav-item mb-2"
                      data-bs-toggle="menu-tooltip"
                    >
                      <i class="fas fa-circle-info me-3" />
                      <span class="d-block">{{ t('helpcenter.title') }}</span>
                    </li>
                  </a>
                  <nuxt-link exact to="/settings">
                    <li
                      :class="{ active: isOpen }"
                      :title="t('settings.title', 2)"
                      class="nav-item mb-2"
                      data-bs-toggle="menu-tooltip"
                    >
                      <i class="fas fa-cog" />
                      <span>{{ t('settings.title', 2) }}</span>
                    </li>
                  </nuxt-link>
                </ul>
              </div>
              <div class="text-center text-muted small mt-2 opacity-50">
                {{ appVersion }}
              </div>
            </div>
          </div>
        </div>
      </nav>
    </div>
    <div class="flex-lg-1 h-screen overflow-y-lg-auto">
      <nav
        id="topbar"
        class="navbar navbar-light border-bottom position-lg-sticky top-lg-0 d-none d-lg-block overlap-10 flex-none bg-white px-0 py-3"
      >
        <div class="container-fluid">
          <div class="navbar-user ms-auto d-none d-sm-block">
            <div class="hstack gap-3 ms-4">
              <LayoutPartsCompanies />
              <LayoutPartsNotifications />
              <LayoutPartsAccount />
            </div>
          </div>
        </div>
      </nav>
      <LayoutPartsNotificationBanners />
      <slot />
      <LayoutPartsAlert />
      <PortalToastContainer />
      <LayoutPartsRoleDebug />
      <LayoutPartsAccessDeniedOverlay />
    </div>
    <ModalsChangeCompany />
  </div>
  <!-- Global modal container -->
  <ModalContainer />
</template>

<style scoped>
.nav-subitem {
  padding-top: 5px !important;
  padding-bottom: 5px !important;
}
.nav-subitem span {
  padding-left: 45px !important;
}
@media (max-width: 992px) {
  /* Adjust the breakpoint according to your needs */
  .nuxt-content {
    padding-top: 100px !important; /* Adjust this value according to the height of your topbar */
  }

  #sidebar {
    background-color: #fff;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1030;
    max-height: 100vh;
    overflow-y: auto;
  }

  /* Add padding to the main content area to prevent overlap */
  .flex-lg-1 {
    padding-top: 80px;
  }

  /* Make sure the collapsed navbar doesn't push content */
  #sidebarCollapse.collapse:not(.show) {
    display: none;
  }

  /* Improve mobile navbar transitions */
  #sidebarCollapse.collapsing {
    position: relative;
    height: 0;
    overflow: hidden;
    transition: height 0.35s ease;
  }
}

/* Additional helper for proper layout on very small screens */
@media (max-width: 576px) {
  .flex-lg-1 {
    padding-top: 60px;
  }
}
</style>
